import * as yup from 'yup';
import { ListingType, ListingSubtype, TransactionMode } from './listing';
import { LoanStatus } from './loan';

export interface DMNotification {
  aps?: {
    alert: DMData;
  };
  data?: DMData;
}

export interface DMData extends Record<string, string | undefined> {
  title: string;
  message: string;
  body: string;
  link: string;
  channel_url: string;
  unread_message_count?: string;
}

export enum NotificationTemplateIdentifier {
  generic = 'generic',
  reminder = 'reminder',
  welcome = 'welcome',
  // 'impact-level' = 'impact-level',
  superChanged = 'superChanged',
}

const superChangedPayloadSchema = yup.object({
  isInitial: yup.boolean().required(),
  isNowSuper: yup.boolean().optional(),
  lostSuper: yup.boolean().optional(),
  keptSuper: yup.boolean().optional(),
});

export type SuperChangedPayloadSchema = yup.InferType<typeof superChangedPayloadSchema>;

const genericPayloadSchema = yup.object({
  title: yup.string().required(),
  titleArgs: yup.object().optional(),
  message: yup.string().required(),
  messageArgs: yup.object().optional(),
  link: yup.string().optional(),
  picture: yup.string().optional(),
  options: yup
    .object({
      favBadge: yup
        .object({
          color: yup.string().required(),
        })
        .optional(),
    })
    .optional(),
});

export type GenericPayloadSchema = yup.InferType<typeof genericPayloadSchema>;

const reminderPayloadSchema = yup.object({
  endDate: yup.string().required(),
  listingName: yup.string().required(),
  listingId: yup.string().optional(), // Was not always included, so it might be undefined for some (old) notifications
  listingType: yup.mixed<ListingType>().oneOf(Object.values(ListingType)).required(),
  listingSubtype: yup.mixed<ListingSubtype>().oneOf(Object.values(ListingSubtype)).optional(), // Was not always included, so it might be undefined for some (old) notifications
  transactionMode: yup.mixed<TransactionMode>().oneOf(Object.values(TransactionMode)).nullable().optional(),
  isDonation: yup.boolean().optional(),
  listingCategory: yup
    .object({
      colorLight: yup.string().required(),
    })
    .defined(undefined)
    .required(),
  picture: yup.string().url().required(),
  channelUrl: yup.string().required(),
  loan: yup
    .object({
      id: yup.string().required(),
      end: yup.string().required(),
      status: yup
        .string()
        .required()
        .oneOf([
          LoanStatus.Accepted,
          LoanStatus.Completed,
          LoanStatus.Pending,
          LoanStatus.Rejected,
          LoanStatus.Returned,
        ]),
    })
    .default(undefined)
    .optional(),
});
export type ReminderPayloadSchema = yup.InferType<typeof reminderPayloadSchema>;

export const notificationSchema = yup.object({
  _id: yup.string().required(),
  createdAt: yup.string().required(),
  seen: yup.boolean().required(),
  templateIdentifier: yup
    .mixed<NotificationTemplateIdentifier>()
    .oneOf(Object.values(NotificationTemplateIdentifier))
    .required(),
  payload: yup.lazy((_value, schemaContext) => {
    switch (schemaContext.parent.templateIdentifier) {
      case NotificationTemplateIdentifier.superChanged:
        return superChangedPayloadSchema;
      case NotificationTemplateIdentifier.generic:
        return genericPayloadSchema;
      case NotificationTemplateIdentifier.reminder:
        return reminderPayloadSchema;
      default:
        return yup.mixed().notRequired();
    }
  }),
});

export const notificationsSchema = yup.object({
  nextPage: yup.number().integer().min(0).required(),
  data: yup.array(notificationSchema).required(),
  totalCount: yup.number().integer().min(0).required(),
});

export type NotificationType = yup.InferType<typeof notificationSchema>;
export type NotificationsType = yup.InferType<typeof notificationsSchema>;

export type GenericNotificationType = Pick<NotificationType, 'createdAt' | 'seen'> & { payload: GenericPayloadSchema };
export type ReminderNotificationType = Omit<NotificationType, 'payload'> & { payload: ReminderPayloadSchema };
export type SuperChangedNotificationType = Pick<NotificationType, 'createdAt' | 'seen'> & {
  payload: SuperChangedPayloadSchema;
};
